import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fromMinor, type Loan, type ScheduleRow } from '@fpc/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { useTdsSections } from '@/hooks/useTdsSections';
import { Modal, Money } from '@/components/ui';
import { describeError } from '@/lib/errors';
import { formatDate, rupeesToMinor } from '@/lib/format';
import {
  FREQUENCY_LABEL,
  LENDER_KIND_LABEL,
  LOAN_KIND_LABEL,
  REPAYMENT_MODE_LABEL,
} from './loanLabels';

/** Rupees as typed, from minor units. */
const rupees = (minor?: number | null) => (minor ? String(fromMinor(minor)) : '');
const percent = (basisPoints?: number | null) => (basisPoints ? String(basisPoints / 100) : '');

/**
 * Adds a loan, or changes a draft one: the lender, the terms its schedule is
 * worked out from, how it is repaid and from which bank account, its charges,
 * TDS and ledgers. The schedule can be previewed before saving.
 */
export function LoanFormModal({
  loan,
  onClose,
  onSaved,
}: {
  loan?: Loan;
  onClose(): void;
  onSaved(id: string): void;
}) {
  const { companyId } = useAuth();
  const queryClient = useQueryClient();
  const { ratesFor, currentCodeFor } = useTdsSections();
  const interestSection = currentCodeFor('194A');
  const tdsRates = ratesFor(interestSection);

  const initial = {
    lenderName: loan?.lender.name ?? '',
    lenderKind: loan?.lender.kind ?? 'BANK',
    pan: loan?.lender.pan ?? '',
    beneficiaryName: loan?.lender.beneficiaryName ?? '',
    accountNumber: loan?.lender.accountNumber ?? '',
    ifsc: loan?.lender.ifsc ?? '',
    loanAccountNumber: loan?.loanAccountNumber ?? '',
    kind: loan?.kind ?? 'TERM',
    bankAccountId: loan?.bankAccountId ?? '',
    sanctioned: rupees(loan?.sanctionedAmount),
    disbursed: rupees(loan?.disbursedAmount),
    disbursedOn: loan?.disbursedOn?.slice(0, 10) ?? '',
    rate: percent(loan?.rateBasisPoints),
    frequency: loan?.frequency ?? 'MONTHLY',
    structure: loan?.structure ?? 'EMI',
    instalments: loan ? String(loan.instalmentCount) : '',
    firstDueDate: loan?.firstDueDate?.slice(0, 10) ?? '',
    moratorium: !!loan?.moratorium,
    moratoriumPeriods: loan?.moratorium ? String(loan.moratorium.periods) : '3',
    moratoriumMode: loan?.moratorium?.mode ?? 'INTEREST_ONLY',
    repaymentMode: loan?.repaymentMode ?? 'AUTO_DEBIT',
    feeAmount: rupees(loan?.processingFee?.amount),
    feeMode: loan?.processingFee?.mode ?? 'DEDUCTED',
    penalRate: percent(loan?.penalRateBasisPoints),
    tdsOn: !!loan?.tds?.applicable,
    tdsRate: loan?.tds?.rateBasisPoints ? String(loan.tds.rateBasisPoints) : '',
    principalLedgerId: loan?.principalLedger?.id ?? '',
    interestLedgerId: loan?.interestLedger?.id ?? '',
    notes: loan?.notes ?? '',
  };
  const [form, setForm] = useState<{
    [K in keyof typeof initial]: (typeof initial)[K] extends boolean ? boolean : string;
  }>(initial);
  const set = (patch: Partial<typeof form>) => setForm((current) => ({ ...current, ...patch }));
  const [preview, setPreview] = useState<ScheduleRow[] | null>(null);

  const { data: accounts } = useQuery({
    queryKey: ['settings-bank-accounts', companyId, 'options'],
    queryFn: () => api.settings.bankAccounts({ companyId, pageSize: 200 }),
  });
  const { data: ledgers } = useQuery({
    queryKey: ['settings-ledgers', companyId, 'general'],
    queryFn: () => api.settings.ledgers({ companyId: companyId!, general: 'true', pageSize: 200 }),
    enabled: !!companyId,
  });
  const ledgerOptions = (type: string) =>
    (ledgers?.items ?? []).filter((ledger) => ledger.active && ledger.type === type);

  const terms = () => ({
    disbursedAmount: rupeesToMinor(form.disbursed) ?? 0,
    rateBasisPoints: Math.round(Number(form.rate || 0) * 100),
    frequency: form.frequency,
    structure: form.structure,
    instalmentCount: Number(form.instalments || 0),
    firstDueDate: form.firstDueDate,
    moratorium: form.moratorium
      ? { periods: Number(form.moratoriumPeriods || 0), mode: form.moratoriumMode }
      : null,
  });

  const body = () => ({
    ...terms(),
    lender: {
      name: form.lenderName,
      kind: form.lenderKind,
      pan: form.pan.trim().toUpperCase(),
      beneficiaryName: form.beneficiaryName,
      accountNumber: form.accountNumber,
      ifsc: form.ifsc.trim().toUpperCase(),
    },
    loanAccountNumber: form.loanAccountNumber,
    kind: form.kind,
    bankAccountId: form.bankAccountId,
    sanctionedAmount: rupeesToMinor(form.sanctioned) ?? 0,
    ...(form.disbursedOn ? { disbursedOn: form.disbursedOn } : {}),
    repaymentMode: form.repaymentMode,
    processingFee: form.feeAmount
      ? { amount: rupeesToMinor(form.feeAmount) ?? 0, mode: form.feeMode }
      : null,
    ...(form.penalRate ? { penalRateBasisPoints: Math.round(Number(form.penalRate) * 100) } : {}),
    tds: form.tdsOn ? { applicable: true, rateBasisPoints: Number(form.tdsRate || 0) } : null,
    principalLedgerId: form.principalLedgerId,
    interestLedgerId: form.interestLedgerId,
    ...(form.notes ? { notes: form.notes } : {}),
  });

  const previewSchedule = useMutation({
    mutationFn: () => api.loans.preview(terms()),
    onSuccess: (result) => setPreview(result.rows),
  });
  const save = useMutation({
    mutationFn: () =>
      loan ? api.loans.update(loan.id, body()) : api.loans.create({ ...body(), companyId }),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['loans'] });
      void queryClient.invalidateQueries({ queryKey: ['loan'] });
      onSaved(saved.id);
    },
  });

  const totals = preview?.reduce(
    (sum, row) => ({ interest: sum.interest + row.interest, paid: sum.paid + row.instalment }),
    { interest: 0, paid: 0 },
  );
  const shown =
    preview && preview.length > 8 ? [...preview.slice(0, 5), null, ...preview.slice(-2)] : preview;

  return (
    <Modal
      title={loan ? `Edit ${loan.reference}` : 'Add loan'}
      onClose={onClose}
      wide
      footer={
        <>
          <button
            type="button"
            className="btn-secondary mr-auto"
            onClick={() => previewSchedule.mutate()}
          >
            {previewSchedule.isPending ? 'Working out…' : 'Preview schedule'}
          </button>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : loan ? 'Save changes' : 'Save as draft'}
          </button>
        </>
      }
    >
      <div className="space-y-5">
        <fieldset className="grid gap-3 sm:grid-cols-3">
          <legend className="mb-2 text-sm font-semibold text-ink-800">Lender</legend>
          <Text
            label="Lender name"
            value={form.lenderName}
            onChange={(lenderName) => set({ lenderName })}
            required
          />
          <Select
            label="Lender type"
            value={form.lenderKind}
            options={LENDER_KIND_LABEL}
            onChange={(lenderKind) =>
              set({ lenderKind, ...(lenderKind === 'BANK' ? { tdsOn: false } : {}) })
            }
          />
          <Text label="Lender PAN" value={form.pan} onChange={(pan) => set({ pan })} />
          {form.repaymentMode === 'BANK_TRANSFER' ? (
            <>
              <Text
                label="Pay to (beneficiary)"
                value={form.beneficiaryName}
                onChange={(beneficiaryName) => set({ beneficiaryName })}
              />
              <Text
                label="Lender's account no."
                value={form.accountNumber}
                onChange={(accountNumber) => set({ accountNumber })}
                required
              />
              <Text label="IFSC" value={form.ifsc} onChange={(ifsc) => set({ ifsc })} />
            </>
          ) : null}
        </fieldset>

        <fieldset className="grid gap-3 sm:grid-cols-3">
          <legend className="mb-2 text-sm font-semibold text-ink-800">Loan</legend>
          <Text
            label="Loan account no."
            value={form.loanAccountNumber}
            onChange={(loanAccountNumber) => set({ loanAccountNumber })}
            required
          />
          <Select
            label="Loan type"
            value={form.kind}
            options={LOAN_KIND_LABEL}
            onChange={(kind) => set({ kind })}
          />
          <label className="block">
            <span className="label">
              Bank account<span className="text-red-600"> *</span>
            </span>
            <select
              aria-label="Bank account"
              className="input"
              value={form.bankAccountId}
              onChange={(event) => set({ bankAccountId: event.target.value })}
            >
              <option value="">Choose…</option>
              {(accounts?.items ?? [])
                .filter((account) => account.active)
                .map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.label} (…{account.accountNumber.slice(-4)})
                  </option>
                ))}
            </select>
          </label>
          <Text
            label="Sanctioned (₹)"
            value={form.sanctioned}
            onChange={(sanctioned) => set({ sanctioned })}
            required
          />
          <Text
            label="Disbursed (₹)"
            value={form.disbursed}
            onChange={(disbursed) => set({ disbursed })}
            required
          />
          <Text
            label="Disbursed on"
            type="date"
            value={form.disbursedOn}
            onChange={(disbursedOn) => set({ disbursedOn })}
          />
          <Text
            label="Interest rate (% a year)"
            value={form.rate}
            onChange={(rate) => set({ rate })}
            required
          />
          <Select
            label="Frequency"
            value={form.frequency}
            options={FREQUENCY_LABEL}
            onChange={(frequency) => set({ frequency })}
          />
          <Select
            label="Structure"
            value={form.structure}
            options={{ EMI: 'Equal EMIs', BULLET: 'Bullet (principal at the end)' }}
            onChange={(structure) => set({ structure })}
          />
          <Text
            label="No. of instalments"
            value={form.instalments}
            onChange={(instalments) => set({ instalments })}
            required
          />
          <Text
            label="First due date"
            type="date"
            value={form.firstDueDate}
            onChange={(firstDueDate) => set({ firstDueDate })}
            required
          />
          <label className="flex items-end gap-2 pb-2 text-sm text-ink-700">
            <input
              type="checkbox"
              checked={form.moratorium}
              onChange={(event) => set({ moratorium: event.target.checked })}
            />
            Moratorium first
          </label>
          {form.moratorium ? (
            <>
              <Text
                label="Moratorium periods"
                value={form.moratoriumPeriods}
                onChange={(moratoriumPeriods) => set({ moratoriumPeriods })}
              />
              <Select
                label="During moratorium"
                value={form.moratoriumMode}
                options={{
                  INTEREST_ONLY: 'Pay interest only',
                  CAPITALISE: 'Add interest to the loan',
                }}
                onChange={(moratoriumMode) => set({ moratoriumMode })}
              />
            </>
          ) : null}
        </fieldset>

        <fieldset className="grid gap-3 sm:grid-cols-3">
          <legend className="mb-2 text-sm font-semibold text-ink-800">
            Repayment, charges and ledgers
          </legend>
          <Select
            label="EMIs paid by"
            value={form.repaymentMode}
            options={REPAYMENT_MODE_LABEL}
            onChange={(repaymentMode) => set({ repaymentMode })}
          />
          <Text
            label="Processing fee (₹)"
            value={form.feeAmount}
            onChange={(feeAmount) => set({ feeAmount })}
          />
          <Select
            label="Fee is"
            value={form.feeMode}
            options={{ DEDUCTED: 'Deducted from disbursement', PAID_SEPARATELY: 'Paid separately' }}
            onChange={(feeMode) => set({ feeMode })}
          />
          <Text
            label="Penal rate (% a year)"
            value={form.penalRate}
            onChange={(penalRate) => set({ penalRate })}
          />
          {form.lenderKind !== 'BANK' ? (
            <>
              <label className="flex items-end gap-2 pb-2 text-sm text-ink-700">
                <input
                  type="checkbox"
                  checked={form.tdsOn}
                  onChange={(event) => set({ tdsOn: event.target.checked })}
                />
                Deduct TDS on interest ({interestSection})
              </label>
              {form.tdsOn ? (
                <label className="block">
                  <span className="label">TDS rate</span>
                  <select
                    aria-label="TDS rate"
                    className="input"
                    value={form.tdsRate}
                    onChange={(event) => set({ tdsRate: event.target.value })}
                  >
                    <option value="">Choose…</option>
                    {tdsRates.map((rate) => (
                      <option key={rate.rateBasisPoints} value={String(rate.rateBasisPoints)}>
                        {rate.rateBasisPoints / 100}% · {rate.label}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </>
          ) : null}
          <label className="block">
            <span className="label">Principal ledger</span>
            <select
              aria-label="Principal ledger"
              className="input"
              value={form.principalLedgerId}
              onChange={(event) => set({ principalLedgerId: event.target.value })}
            >
              <option value="">Not tagged</option>
              {ledgerOptions('LIABILITY').map((ledger) => (
                <option key={ledger.id} value={ledger.id}>
                  {ledger.code} · {ledger.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="label">Interest ledger</span>
            <select
              aria-label="Interest ledger"
              className="input"
              value={form.interestLedgerId}
              onChange={(event) => set({ interestLedgerId: event.target.value })}
            >
              <option value="">Not tagged</option>
              {ledgerOptions('EXPENSE').map((ledger) => (
                <option key={ledger.id} value={ledger.id}>
                  {ledger.code} · {ledger.name}
                </option>
              ))}
            </select>
          </label>
          <Text label="Notes" value={form.notes} onChange={(notes) => set({ notes })} />
        </fieldset>

        {previewSchedule.error ? (
          <p className="text-sm text-red-700">{describeError(previewSchedule.error)}</p>
        ) : null}
        {shown && totals ? (
          <section aria-label="Schedule preview" className="rounded-lg border border-ink-100">
            <p className="border-b border-ink-100 bg-ink-50 px-3 py-2 text-sm">
              {preview!.length} instalments · interest <Money minor={totals.interest} /> · total
              repaid <Money minor={totals.paid} />
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-ink-500">
                    <th className="px-3 py-1.5">#</th>
                    <th className="px-3 py-1.5">Due</th>
                    <th className="px-3 py-1.5 text-right">Principal</th>
                    <th className="px-3 py-1.5 text-right">Interest</th>
                    <th className="px-3 py-1.5 text-right">EMI</th>
                    <th className="px-3 py-1.5 text-right">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((row, index) =>
                    row ? (
                      <tr key={row.n} className="border-t border-ink-100">
                        <td className="px-3 py-1.5">{row.n}</td>
                        <td className="px-3 py-1.5">{formatDate(row.dueDate)}</td>
                        <td className="px-3 py-1.5 text-right">
                          <Money minor={row.principal} />
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          <Money minor={row.interest} />
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          <Money minor={row.instalment} />
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          <Money minor={row.closing} />
                        </td>
                      </tr>
                    ) : (
                      <tr key={`gap-${index}`}>
                        <td colSpan={6} className="px-3 py-1 text-center text-xs text-ink-400">
                          …
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}

        {save.error ? <p className="text-sm text-red-700">{describeError(save.error)}</p> : null}
      </div>
    </Modal>
  );
}

function Text({
  label,
  value,
  onChange,
  type = 'text',
  required = false,
}: {
  label: string;
  value: string;
  onChange(value: string): void;
  type?: string;
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="label">
        {label}
        {required ? <span className="text-red-600"> *</span> : null}
      </span>
      <input
        className="input"
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function Select({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Record<string, string>;
  onChange(value: string): void;
}) {
  return (
    <label className="block">
      <span className="label">{label}</span>
      <select
        aria-label={label}
        className="input"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {Object.entries(options).map(([key, text]) => (
          <option key={key} value={key}>
            {text}
          </option>
        ))}
      </select>
    </label>
  );
}
